import { useState, useRef, useCallback, useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { api } from './useApi';

interface NativeRecorderPlugin {
  startRecording(): Promise<void>;
  stopRecording(): Promise<{ path: string }>;
  isRecording(): Promise<{ recording: boolean }>;
  enableRemoteToggle(): Promise<void>;
  disableRemoteToggle(): Promise<void>;
  getPendingRecording(): Promise<{ path: string | null }>;
  addListener(event: 'audioChunk', cb: (data: { data: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'remoteToggle', cb: () => void): Promise<PluginListenerHandle>;
  addListener(event: 'recordingInterrupted', cb: () => void): Promise<PluginListenerHandle>;
}

export const NativeRecorder = registerPlugin<NativeRecorderPlugin>('NativeRecorder');

interface Options {
  onCommit: (text: string) => void;
  onSilence?: () => void;   // called after VAD silence following last committed chunk
  silenceTimeoutMs?: number; // default 2500
  deviceId?: string;
}

export function useRealtimeTranscription({ onCommit, onSilence, silenceTimeoutMs = 2500, deviceId }: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [partialText, setPartialText] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const nativeListenerRef = useRef<PluginListenerHandle | null>(null);
  const remoteListenerRef = useRef<PluginListenerHandle | null>(null);
  const recordingRef = useRef(false);
  const startRef = useRef<() => Promise<void>>();
  const stopRef = useRef<() => Promise<void>>();
  const busyRef = useRef(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSilenceRef = useRef(onSilence);
  onSilenceRef.current = onSilence;

  const startTranscription = useCallback(async () => {
    if (wsRef.current || busyRef.current) return;
    busyRef.current = true;
    try {
      const { token } = await api.getTranscribeToken();

      const ws = new WebSocket(
        `wss://api.elevenlabs.io/v1/speech-to-text/realtime?token=${token}&model_id=scribe_v2_realtime&language_code=en&encoding=pcm_16000&sample_rate=16000&commit_strategy=vad`
      );
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        console.log('[STT] received:', msg);
        if (msg.message_type === 'partial_transcript') {
          const partial = msg.text || '';
          setPartialText(partial);
          // Only reset silence timer for real speech (ignore noise annotations like "(wind blowing)")
          const isRealSpeech = partial.trim() && !/^\(.*\)$/.test(partial.trim());
          if (isRealSpeech && silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        } else if (msg.message_type === 'committed_transcript') {
          const text = msg.text || '';
          if (text.trim()) {
            onCommit(text);
            // Start silence timer after speech is committed
            if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = setTimeout(() => {
              silenceTimerRef.current = null;
              onSilenceRef.current?.();
            }, silenceTimeoutMs);
          }
          setPartialText('');
        }
      };

      ws.onerror = () => {
        // Ignore errors during intentional close
      };

      ws.onclose = (event) => {
        console.log('[STT] WebSocket closed:', event.code, event.reason);
        setPartialText('');
      };

      await new Promise<void>((resolve, reject) => {
        ws.addEventListener('open', () => resolve(), { once: true });
        ws.addEventListener('error', () => reject(new Error('WebSocket failed to connect')), { once: true });
      });

      if (Capacitor.isNativePlatform()) {
        await NativeRecorder.startRecording();

        nativeListenerRef.current = await NativeRecorder.addListener('audioChunk', (event) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          ws.send(JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: event.data,
            commit: false,
            sample_rate: 16000,
          }));
        });
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });

        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          }
        } catch (e) {
          console.warn('[STT] Wake lock request failed:', e);
        }
        streamRef.current = stream;

        const audioContext = new AudioContext({ sampleRate: 16000 });
        contextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);

        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        let chunkCount = 0;
        processor.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          chunkCount++;
          if (chunkCount % 20 === 1) console.log('[STT] sending audio chunk #', chunkCount);
          const float32 = e.inputBuffer.getChannelData(0);
          const int16 = new Int16Array(float32.length);
          for (let i = 0; i < float32.length; i++) {
            const s = Math.max(-1, Math.min(1, float32[i]));
            int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
          const bytes = new Uint8Array(int16.buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          ws.send(JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: btoa(binary),
            commit: false,
            sample_rate: 16000,
          }));
        };

        source.connect(processor);
        processor.connect(audioContext.destination);
      }

      setIsRecording(true);
    } catch (err: any) {
      alert('Recording failed: ' + err.message);
      wsRef.current?.close();
      wsRef.current = null;
    } finally {
      busyRef.current = false;
    }
  }, [onCommit, deviceId]);

  const stopTranscription = useCallback(async (): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    try {
      wakeLockRef.current?.release();
      wakeLockRef.current = null;

      // Stop audio capture first
      if (Capacitor.isNativePlatform()) {
        await NativeRecorder.stopRecording().catch(() => {});
        // Small delay to let final chunks flush through the listener
        await new Promise(r => setTimeout(r, 300));
        nativeListenerRef.current?.remove();
        nativeListenerRef.current = null;
      } else {
        processorRef.current?.disconnect();
        processorRef.current = null;
        contextRef.current?.close();
        contextRef.current = null;
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      const ws = wsRef.current;
      wsRef.current = null;

      if (ws && ws.readyState === WebSocket.OPEN) {
        // Send final commit and wait for the committed_transcript before closing
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => { ws.close(); resolve(); }, 3000);
          const origOnMessage = ws.onmessage;
          ws.onmessage = (event) => {
            if (origOnMessage) (origOnMessage as any).call(ws, event);
            const msg = JSON.parse(event.data);
            if (msg.message_type === 'committed_transcript') {
              clearTimeout(timeout);
              ws.close();
              resolve();
            }
          };
          ws.send(JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: '',
            commit: true,
            sample_rate: 16000,
          }));
        });
      }

      setIsRecording(false);
      setPartialText('');
    } finally {
      busyRef.current = false;
    }
  }, []);

  // Keep recording ref in sync
  recordingRef.current = isRecording;

  // Remote toggle support: consumer sets onRemoteToggle to their full toggle handler
  const remoteToggleRef = useRef<(() => void) | null>(null);
  const setRemoteToggle = useCallback((handler: () => void) => {
    remoteToggleRef.current = handler;
  }, []);

  // Set up Bluetooth/headset button toggle on native
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener: PluginListenerHandle | null = null;

    let interruptListener: PluginListenerHandle | null = null;

    (async () => {
      await NativeRecorder.enableRemoteToggle();
      listener = await NativeRecorder.addListener('remoteToggle', () => {
        remoteToggleRef.current?.();
      });
      remoteListenerRef.current = listener;

      // If another app takes audio, auto-trigger stop+save
      interruptListener = await NativeRecorder.addListener('recordingInterrupted', () => {
        if (recordingRef.current) {
          remoteToggleRef.current?.();
        }
      });
    })();

    return () => {
      listener?.remove();
      interruptListener?.remove();
      NativeRecorder.disableRemoteToggle().catch(() => {});
    };
  }, []);

  return { isRecording, partialText, startTranscription, stopTranscription, setRemoteToggle };
}
